import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { GoSegmentedToggleOption } from './models/go-segmented-toggle-option';

@Component({
    selector: 'app-go-segmented-toggle',
    imports: [
        MatExpansionModule,
        MatRadioModule,
        MatIconModule,
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        CommonModule,
        MatFormFieldModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GoSegmentedToggleComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './go-segmented-toggle.component.html',
})
export class GoSegmentedToggleComponent<T> implements ControlValueAccessor {
    @Input() allowMultipleSelections: boolean = false;
    @Input() options: GoSegmentedToggleOption<T>[] = [];

    private selectedSet = new Set<T>();

    private readonly compareWith: (a: T, b: T) => boolean = (a, b) =>
        a === (b as T);

    constructor(private cdr: ChangeDetectorRef) {}

    private onChange: (value: T | T[] | null) => void = () => {};
    private onTouched: () => void = () => {};
    private isDisabled = false;

    isOptionDisabled(option: GoSegmentedToggleOption<T>): boolean {
        return !!option.isDisabled?.();
    }

    isSelected(value: T): boolean {
        for (const v of this.selectedSet) {
            if (this.compareWith(v, value)) return true;
        }

        return false;
    }

    onValueSelected(value: T): void {
        if (this.isDisabled) {
            return;
        }

        if (this.allowMultipleSelections) {
            this.handleMultipleSelection(value);
        } else {
            this.handleSingleSelection(value);
        }

        this.onTouched();
    }

    writeValue(value: T | T[] | null): void {
        this.selectedSet.clear();

        if (Array.isArray(value)) {
            value.forEach((v) => this.selectedSet.add(v));
        } else if (value != null) {
            this.selectedSet.add(value);
        }

        // If incoming value is an array but allow multiple is false,
        // pick the first element to render a stable UI.
        if (!this.allowMultipleSelections && this.selectedSet.size > 1) {
            const [first] = this.selectedSet;

            this.selectedSet.clear();

            if (first !== undefined) {
                this.selectedSet.add(first);
            }
        }

        this.cdr.detectChanges();
    }

    registerOnChange(fn: (value: T | T[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        this.cdr.markForCheck();
    }

    private handleMultipleSelection(value: T): void {
        if (this.isSelected(value)) {
            this.deleteFromSet(value);
        } else {
            this.selectedSet.add(value);
        }
        this.emit();
    }

    private handleSingleSelection(value: T): void {
        this.selectedSet.clear();
        this.selectedSet.add(value);
        this.emit();
    }

    private emit(): void {
        if (this.allowMultipleSelections) {
            this.onChange(Array.from(this.selectedSet));
        } else {
            const [first] = this.selectedSet;
            this.onChange(first ?? null);
        }
    }

    private deleteFromSet(value: T): void {
        for (const v of this.selectedSet) {
            if (this.compareWith(v, value)) {
                this.selectedSet.delete(v);
                return;
            }
        }
    }
}
